import React, { useEffect, useState } from "react";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { Tooltip } from "@mui/material";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { GridRowModes } from "@mui/x-data-grid";

import { FaCheck } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import { MdDelete } from "react-icons/md";
import { TfiPencilAlt } from "react-icons/tfi";
import { IconButton } from "@material-ui/core";

import { useWizard } from "components/wizard/WizardContext";
import { packageCode } from "../../materialDistribution/helper";
import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import { editCellContainerStyle } from "../onBoardingMaterialIdSetup/constant";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import SelectMaterialElasticSearch from "../onBoardingMaterialIdSetup/SelectMaterialElasticSearch";
import {
  failureToast,
  successToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";

import { inputHeight, NEW_EMPTY_MATERIAL_ROW } from "./helper";
import {
  joinCourseIds,
  editCourseMaterialMapping,
  createNewMapping,
} from "./utilsFunctions";

const SelectMaterialIdStep = () => {
  const { id } = useParams();
  const history = useHistory();
  const { updateValues, values, back } = useWizard();
  const { materialInfo } = values;

  const [rowForm, setRowForm] = useState({});
  const [rowModesModel, setRowModesModel] = useState({});
  const [pkgOptions, setPkgOptions] = useState(packageCode);

  useEffect(() => {
    if (rowForm.id) {
      const usedPkgCodes = materialInfo
        .filter(({ packageCodeId }) => packageCodeId)
        .map((item) => item.packageCodeId)
        .filter((id) => id !== rowForm.packageCodeId);

      const filteredPkgCodes = packageCode.filter(
        (pkg) => !usedPkgCodes.includes(pkg)
      );
      setPkgOptions(filteredPkgCodes);
    }
  }, [rowForm?.packageCodeId]);

  const startEditing = (id, form) => {
    setRowModesModel((oldModel) => ({
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "packageCodeId" },
    }));

    setRowForm(form);
  };

  const stopEditing = () => {
    const currForm = { ...rowForm };

    setRowModesModel((oldModel) => ({
      [currForm?.id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
    setRowForm({});
  };

  const newRow = () => {
    if (!_.isEmpty(rowForm)) {
      failureToast("Please Delete or Save current changes");
    } else {
      let randomId = uuidv4();
      let emptyRow = {
        ...NEW_EMPTY_MATERIAL_ROW,
        id: randomId,
      };
      startEditing(randomId, emptyRow);
      const addNewRow = [emptyRow, ...materialInfo];
      updateValues("materialInfo", addNewRow);
    }
  };

  const deleteRow = (rowId) => {
    const filteredRow = materialInfo.filter((item) => item.id !== rowId);
    updateValues("materialInfo", filteredRow);
  };

  const handleEditClick = (params) => {
    if (!_.isEmpty(rowForm)) {
      failureToast("Please Delete or Save current changes");
    } else {
      startEditing(params?.id, params?.row);
    }
  };

  const handleCancelClick = async (params) => {
    if (params?.row?.isNew) {
      const filteredRow = materialInfo.slice(1);
      updateValues("materialInfo", filteredRow);
    }

    stopEditing();
  };

  const saveEntry = () => {
    if (!rowForm["packageCodeId"]) {
      failureToast("Please select Package Code");
      return;
    } else if (!rowForm["materialIds"].length) {
      failureToast("Please select atleast one Material");
      return;
    }

    const updatedRows = materialInfo.map((item) =>
      item.id === rowForm.id
        ? {
            //  set isNew to false or delete the key
            packageCodeId: rowForm.packageCodeId,
            materialIds: rowForm.materialIds,
            id: item.id,
          }
        : item
    );

    updateValues("materialInfo", updatedRows);
    stopEditing();
  };

  const rowFormHandler = (value, key) => {
    setRowForm((prev) => ({ ...prev, [key]: value }));
  };

  const columns = [
    new AGridColDef("packageCodeId", "Package Code")
      .setFlex(0.2)
      .editable(true)
      .renderCellComponent((params) => (
        <div className="ellipsis-text-container">{params?.value}</div>
      ))
      .renderEditCellComponent((params) => (
        <div style={editCellContainerStyle}>
          <AAutoComplete
            placeholder="Select"
            height={inputHeight}
            items={pkgOptions.map((code) => ({
              label: code,
              value: code,
            }))}
            isShowCount={false}
            isMulti={false}
            currentValue={
              rowForm?.packageCodeId
                ? {
                    label: rowForm?.packageCodeId,
                    value: rowForm?.packageCodeId,
                  }
                : ""
            }
            handleChange={(value) => {
              rowFormHandler(value.value, "packageCodeId");
              rowFormHandler([], "materialIds");
            }}
            inputKey="packageCodeId"
          />
        </div>
      )),

    new AGridColDef("materialIds", "Material IDs")
      .setFlex(0.6)
      .editable(true)
      .renderCellComponent((params) => (
        <Tooltip title={joinCourseIds(params.value)}>
          <div className="ellipsis-text-container">
            {joinCourseIds(params.value)}
          </div>
        </Tooltip>
      ))
      .renderEditCellComponent((params) => (
        <div style={editCellContainerStyle}>
          <SelectMaterialElasticSearch
            currentValue={rowForm?.materialIds || []}
            handleChange={(value) => {
              // const savedValues = value?.map(({ value }) => value);
              rowFormHandler(value, "materialIds");
            }}
            isMulti={true}
            height={inputHeight}
            dropdownMappingParams={[
              "materialIdAndDescription",
              "materialId",
              "materialDescription",
            ]}
            filters={{ categoryDispValue: { value: rowForm?.packageCodeId } }}
            isDisabled={!rowForm?.packageCodeId}
          />
        </div>
      )),

    new AGridColDef("actions", "Action", false)
      .setFlex(0.2)
      .renderCellComponent((params) =>
        params.api.getRowMode(params?.id) === "view" ? (
          <>
            <Tooltip title="Edit">
              <IconButton
                className="p-0"
                onClick={() => {
                  handleEditClick(params);
                }}
              >
                <TfiPencilAlt color="#00B0F5" size="16px" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                className="p-0"
                onClick={() => {
                  deleteRow(params.row.id);
                }}
              >
                <MdDelete color="red" size="18px" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Save">
              <IconButton
                className="p-0"
                onClick={() => {
                  saveEntry(params);
                }}
              >
                <FaCheck color="green" size="16px" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Cancel">
              <IconButton
                className="p-0"
                onClick={() => {
                  handleCancelClick(params);
                }}
              >
                <RxCross2 color="red" size="18px" />
              </IconButton>
            </Tooltip>
          </>
        )
      ),
  ];

  const saveValues = async () => {
    let response;

    if (id) {
      response = await editCourseMaterialMapping({
        id,
        payload: values,
      });
    } else {
      response = await createNewMapping(values);
    }

    if (response.code === 200) {
      if (id) successToast("Edited Successfully");
      else successToast("Mapping created Successfully");

      history.push("/admin/edp/courseMaterialListing");
    }
  };

  const isSaveDisabled =
    !_.isEmpty(rowForm) || !materialInfo.filter((item) => !item?.isNew).length;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="text-bold">Select Material ID</h2>
        <AButton
          size="xs"
          onClick={() => {
            newRow();
          }}
          variant="primary_filled"
        >
          + Add New Package
        </AButton>
      </div>

      <div className="mt-3" style={{ height: "37vh" }}>
        <ADataGrid
          removeWrapperContainer
          rowModesModel={rowModesModel}
          rows={materialInfo || []}
          columns={columns}
          rowId={(row) => row?.id}
          rowHeight={50}
          loading={false}
          rowCount={materialInfo.length}
          hidePagination
        />
      </div>

      <footer className="mt-2 d-flex justify-content-between">
        <AButton
          onClick={() => {
            //  go to Step 2
            back(1);
          }}
          className="button_remove_left_margin"
          variant="outline"
          disabled={!_.isEmpty(rowForm)}
        >
          Back
        </AButton>

        <AButton
          disabled={isSaveDisabled}
          onClick={saveValues}
          className="button_remove_left_margin"
          variant="primary_filled"
        >
          Save
        </AButton>
      </footer>
    </>
  );
};

export default SelectMaterialIdStep;
